import { CartAddEvent, CartUpdateEvent } from '@theme/events';
import { prepareCartDrawerForAdd, getCartItemCount } from '@theme/cart-drawer-section';

/**
 * Fridge Fill lander — mix of N in-stock 12-packs, one add-to-cart.
 */

function getCartSectionIds() {
  const ids = Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
    .filter(Boolean);
  if (!ids.includes('cart-drawer')) ids.push('cart-drawer');
  return ids;
}

function formatMoney(cents) {
  const value = Number(cents) / 100;
  if (!Number.isFinite(value)) return '';
  if (Number.isInteger(value)) return `$${value}`;
  return `$${value.toFixed(2)}`;
}

function formatDelivered(cents) {
  const money = formatMoney(cents);
  return money ? `${money} delivered` : '';
}

function readConfig(root) {
  const node = root.querySelector('[data-ff-config]');
  if (!node) return null;
  try {
    return JSON.parse(node.textContent || '{}');
  } catch (err) {
    return null;
  }
}

function getQty(root, handle) {
  const card = root.querySelector(`[data-ff-card][data-ff-handle="${handle}"]`);
  if (card) {
    const fromCard = parseInt(card.getAttribute('data-ff-count') || '', 10);
    if (Number.isFinite(fromCard) && fromCard >= 0) return fromCard;
  }
  const input = root.querySelector(`[data-ff-qty][data-ff-handle="${handle}"]`);
  const n = input ? parseInt(input.value, 10) : 0;
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function qtyLabel(qty) {
  return qty === 1 ? '1 box' : `${qty} boxes`;
}

function setQty(root, handle, qty) {
  const input = root.querySelector(`[data-ff-qty][data-ff-handle="${handle}"]`);
  const card = root.querySelector(`[data-ff-card][data-ff-handle="${handle}"]`);
  if (input) input.value = String(qty);
  if (card) {
    card.setAttribute('data-ff-count', String(qty));
    const display = card.querySelector('[data-ff-qty-display]');
    if (display) display.textContent = qtyLabel(qty);
    const minus = card.querySelector('[data-ff-minus]');
    if (minus) minus.disabled = qty <= 0;
    card.classList.toggle('is-selected', qty > 0);
  }
}

function totalQty(root, flavours) {
  return flavours.reduce((sum, flavour) => sum + getQty(root, flavour.handle), 0);
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function selectedFlavours(root, config) {
  const ranked = [];
  config.flavours.forEach((flavour) => {
    const qty = getQty(root, flavour.handle);
    if (qty > 0) ranked.push({ flavour, qty });
  });
  ranked.sort((a, b) => {
    const rank = (slug) => {
      if (slug === 'mango') return 0;
      if (slug === 'pear') return 1;
      return 2;
    };
    const bySlug = rank(a.flavour.slug || '') - rank(b.flavour.slug || '');
    if (bySlug !== 0) return bySlug;
    return b.qty - a.qty;
  });
  return ranked;
}

function mixSummary(root, config) {
  return selectedFlavours(root, config)
    .map(({ flavour, qty }) => `${flavour.name}:${qty}`)
    .join(',');
}

function updateSlots(root, config) {
  const list = root.querySelector('[data-ff-slots]');
  if (!list) return;

  const filled = [];
  // Mango then Pear fill order so slots match summer default mix
  const flavourRank = (slug) => {
    if (slug === 'mango') return 0;
    if (slug === 'pear') return 1;
    return 2;
  };
  const ordered = [...config.flavours].sort(
    (a, b) => flavourRank(a.slug || '') - flavourRank(b.slug || '')
  );
  ordered.forEach((flavour) => {
    const qty = getQty(root, flavour.handle);
    for (let i = 0; i < qty; i += 1) filled.push(flavour);
  });

  const parts = [];
  for (let index = 0; index < config.target; index += 1) {
    const flavour = filled[index];
    if (!flavour) {
      parts.push(
        `<li class="ff__slot is-empty" data-ff-slot><span class="ff__slot-label">Box ${index + 1}</span></li>`
      );
      continue;
    }
    const slug = escapeHtml(flavour.slug || 'soda');
    const name = escapeHtml(flavour.name || 'Box');
    parts.push(
      `<li class="ff__slot is-flavour-${slug}" data-ff-slot><span class="ff__slot-label">${name}</span></li>`
    );
  }
  list.innerHTML = parts.join('');
}

function updateHeroCans(root, config) {
  const host = root.querySelector('[data-ff-hero-cans]');
  if (!host) return;

  const cans = [];
  const flavourRank = (slug) => {
    if (slug === 'mango') return 0;
    if (slug === 'pear') return 1;
    return 2;
  };
  const ordered = [...config.flavours].sort(
    (a, b) => flavourRank(a.slug || '') - flavourRank(b.slug || '')
  );
  for (const flavour of ordered) {
    const qty = getQty(root, flavour.handle);
    const url = flavour.clearcut || '';
    if (!url || qty <= 0) continue;
    for (let i = 0; i < qty; i += 1) {
      if (cans.length >= config.target) break;
      cans.push({ url, slug: flavour.slug || 'soda' });
    }
    if (cans.length >= config.target) break;
  }

  if (!cans.length) {
    host.innerHTML = '';
    host.hidden = true;
    return;
  }

  host.hidden = false;
  host.innerHTML = cans
    .map(
      (can, index) =>
        `<img class="ff__hero-can is-flavour-${escapeHtml(can.slug)}" src="${escapeHtml(can.url)}" alt="" width="220" height="420" decoding="async" style="--ff-can-i:${index}" />`
    )
    .join('');
}

function updateCherryNudge(root, config) {
  const nudge = root.querySelector('[data-ff-cherry-nudge]');
  if (!nudge) return;
  const mango = config.flavours.find((flavour) => (flavour.slug || '') === 'mango');
  if (!mango) {
    nudge.hidden = true;
    return;
  }
  const boxes = totalQty(root, config.flavours);
  const mangoQty = getQty(root, mango.handle);
  nudge.hidden = !(boxes > 0 && mangoQty === 0);
}

function updateTotals(root, config) {
  const boxes = totalQty(root, config.flavours);
  const needed = Math.max(0, config.target - boxes);
  const listCents = config.flavours.reduce((sum, flavour) => {
    return sum + flavour.price * getQty(root, flavour.handle);
  }, 0);
  const discountOn = config.discountEnabled && boxes >= config.discountMin && config.discountCents > 0;
  const discountCents = discountOn ? config.discountCents : 0;
  const netCents = Math.max(0, listCents - discountCents);
  const shipFree = listCents >= config.thresholdCents;
  const complete = boxes === config.target;

  const totalEls = root.querySelectorAll('[data-ff-total]');
  totalEls.forEach((el) => {
    el.textContent = formatMoney(netCents);
  });

  const ctaEls = root.querySelectorAll('[data-ff-add]');
  ctaEls.forEach((el) => {
    const label = el.querySelector('[data-ff-add-label]');
    if (label) {
      if (complete) {
        label.textContent = `Add ${boxes} boxes · ${formatDelivered(netCents)}`;
      } else if (boxes === 0) {
        label.textContent = `Pick ${config.target} boxes`;
      } else {
        label.textContent = `Add ${needed} more to unlock`;
      }
    }
    el.disabled = !complete;
  });

  const nowEls = root.querySelectorAll('[data-ff-now]');
  nowEls.forEach((el) => {
    const amount = netCents || listCents;
    el.textContent = complete || discountOn ? formatDelivered(amount) : formatMoney(amount);
  });

  const listEls = root.querySelectorAll('[data-ff-list]');
  listEls.forEach((el) => {
    const showWas = discountOn && listCents > netCents && listCents > 0;
    el.hidden = !showWas;
    if (showWas) el.textContent = formatMoney(listCents);
  });

  const afterpayEls = root.querySelectorAll('[data-ff-afterpay]');
  afterpayEls.forEach((el) => {
    const base = complete || discountOn ? netCents : listCents;
    if (!base) {
      el.hidden = true;
      return;
    }
    const fortnight = Math.round(base / 4);
    el.hidden = false;
    el.textContent = `or 4 × ${formatMoney(fortnight)} / fortnight with Afterpay`;
  });

  const note = root.querySelector('[data-ff-status-note]');
  if (note) {
    if (!complete && boxes > 0) {
      note.textContent = ` · ${needed} more to complete your fill`;
    } else if (!complete) {
      note.textContent = ` · Choose ${config.target} boxes`;
    } else {
      note.textContent = '';
    }
  }

  const unlocked = root.querySelector('[data-ff-unlocked]');
  const unlockedValue = root.querySelector('[data-ff-unlocked-value]');
  if (unlocked) {
    unlocked.hidden = !complete;
    if (complete && unlockedValue) {
      const cans = (config.target || 3) * 12;
      const parts = [];
      if (shipFree) parts.push('free NZ shipping');
      if (discountOn) parts.push(config.discountLabel || '$6 off');
      parts.push(`${cans} cans`);
      unlockedValue.textContent = parts.join(' · ');
    }
  }

  const live = root.querySelector('[data-ff-live]');
  if (live) live.remove();

  config.flavours.forEach((flavour) => {
    const card = root.querySelector(`[data-ff-card][data-ff-handle="${flavour.handle}"]`);
    if (!card) return;
    const plus = card.querySelector('[data-ff-plus]');
    if (plus) plus.disabled = getQty(root, flavour.handle) >= config.target;
  });

  updateSlots(root, config);
  updateHeroCans(root, config);
  updateCherryNudge(root, config);
}

function itemsFromMix(root, config) {
  const summary = mixSummary(root, config);
  const boxes = totalQty(root, config.flavours);
  return config.flavours
    .map((flavour) => {
      const quantity = getQty(root, flavour.handle);
      if (!quantity || !flavour.variantId) return null;
      return {
        id: flavour.variantId,
        quantity,
        properties: {
          _source: 'fridge-fill',
          _ff_mix: summary,
          _ff_boxes: String(boxes),
          _ff_flavour: flavour.name || flavour.slug || '',
        },
      };
    })
    .filter(Boolean);
}

function trackMixAdd(root, config) {
  const payload = {
    event: 'fridge_fill_add',
    boxes: totalQty(root, config.flavours),
    mix: mixSummary(root, config),
    flavours: selectedFlavours(root, config).map(({ flavour, qty }) => ({
      handle: flavour.handle,
      slug: flavour.slug,
      qty,
    })),
  };
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  } catch (_) {
    // ignore
  }
  try {
    document.dispatchEvent(new CustomEvent('sidekick:fridge-fill-add', { detail: payload }));
  } catch (_) {
    // ignore
  }
}

function openCartDrawer() {
  const cartDrawer = document.querySelector('cart-drawer-component[auto-open]');
  if (!(cartDrawer instanceof HTMLElement) || !('showDialog' in cartDrawer)) return;
  try {
    /** @type {any} */ (cartDrawer).showDialog();
  } catch (_) {
    const dialog = cartDrawer.querySelector('dialog');
    if (dialog instanceof HTMLDialogElement) dialog.open = true;
  }
}

async function syncCartBubble(sections) {
  try {
    const cart = await fetch('/cart.js').then((r) => r.json());
    const serverCount = cart?.item_count ?? 0;
    if (serverCount === getCartItemCount()) return;
    document.dispatchEvent(
      new CartUpdateEvent(cart, 'fridge-fill-sync', {
        source: 'cart-sync',
        itemCount: serverCount,
        sections,
      })
    );
  } catch (_) {
    // ignore
  }
}

async function addItems(root, items, button, errorNode, trackingConfig) {
  if (!items.length) return;

  const cartDrawer = document.querySelector('cart-drawer-component[auto-open]');
  prepareCartDrawerForAdd(cartDrawer);

  const originalBusy = button.getAttribute('aria-busy');
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');

  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items,
        sections: getCartSectionIds().join(','),
        sections_url: window.location.pathname,
      }),
    });
    const data = await res.json();
    if (!res.ok || data.status) {
      throw new Error(data.description || data.message || 'Could not add to cart');
    }
    document.dispatchEvent(
      new CartAddEvent(data, 'fridge-fill', {
        source: 'fridge-fill-landing',
        itemCount: data.item_count ?? 0,
        sections: data.sections,
      })
    );
    if (trackingConfig) trackMixAdd(root, trackingConfig);
    openCartDrawer();
    await syncCartBubble(data.sections);
  } catch (err) {
    const status = errorNode || root.querySelector('[data-ff-status]');
    if (status instanceof HTMLElement) {
      status.textContent = err instanceof Error ? err.message : 'Could not add to cart. Try again.';
    }
  } finally {
    button.disabled = false;
    if (originalBusy == null) button.removeAttribute('aria-busy');
    else button.setAttribute('aria-busy', originalBusy);
    cartDrawer?.classList.remove('cart-drawer--pending');
  }
}

async function addMix(root, config, button) {
  const items = itemsFromMix(root, config);
  await addItems(root, items, button, root.querySelector('[data-ff-status]'), config);
  updateTotals(root, config);
}

async function addPreorder(root, config, button) {
  const preorder = config.preorder;
  if (!preorder || !preorder.variantId) return;
  await addItems(
    root,
    [
      {
        id: preorder.variantId,
        quantity: 1,
        properties: { _source: 'fridge-fill-watermelon' },
      },
    ],
    button,
    root.querySelector('[data-ff-preorder-status]'),
    null
  );
}

function applyUrlMix(root, config) {
  const params = new URLSearchParams(window.location.search);
  const bySlug = new Map();
  config.flavours.forEach((flavour) => {
    if (flavour.slug) bySlug.set(String(flavour.slug).toLowerCase(), flavour);
  });

  const requested = new Map();
  bySlug.forEach((_, slug) => {
    if (!params.has(slug)) return;
    const n = parseInt(params.get(slug) || '', 10);
    if (Number.isFinite(n) && n > 0) requested.set(slug, n);
  });

  const mixRaw = params.get('mix');
  if (mixRaw) {
    mixRaw.split(',').forEach((part) => {
      const [slugRaw, qtyRaw] = part.split(':');
      const slug = (slugRaw || '').trim().toLowerCase();
      const n = parseInt(qtyRaw || '', 10);
      if (!slug || !bySlug.has(slug) || !Number.isFinite(n) || n <= 0) return;
      requested.set(slug, n);
    });
  }

  if (!requested.size) return false;

  config.flavours.forEach((flavour) => setQty(root, flavour.handle, 0));

  let remaining = config.target;
  requested.forEach((qty, slug) => {
    if (remaining <= 0) return;
    const flavour = bySlug.get(slug);
    if (!flavour) return;
    const next = Math.min(qty, remaining, config.target);
    setQty(root, flavour.handle, next);
    remaining -= next;
  });

  return true;
}

function initRoot(root) {
  if (root.dataset.ffInit === '1') return;
  const config = readConfig(root);
  if (!config || !Array.isArray(config.flavours) || !config.flavours.length) return;
  root.dataset.ffInit = '1';

  config.flavours.forEach((flavour) => {
    setQty(root, flavour.handle, flavour.qty || 0);
  });
  applyUrlMix(root, config);
  updateTotals(root, config);

  root.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const preorderAdd = target.closest('[data-ff-preorder-add]');
    if (preorderAdd instanceof HTMLButtonElement) {
      event.preventDefault();
      addPreorder(root, config, preorderAdd);
      return;
    }

    const add = target.closest('[data-ff-add]');
    if (add instanceof HTMLButtonElement) {
      event.preventDefault();
      addMix(root, config, add);
      return;
    }

    const minus = target.closest('[data-ff-minus]');
    const plus = target.closest('[data-ff-plus]');
    const control = minus || plus;
    if (!(control instanceof HTMLButtonElement)) return;

    const card = control.closest('[data-ff-card]');
    if (!card) return;
    const handle = card.getAttribute('data-ff-handle');
    if (!handle) return;

    const current = getQty(root, handle);
    if (minus) {
      setQty(root, handle, Math.max(0, current - 1));
    } else if (plus) {
      if (current >= config.target) return;
      if (totalQty(root, config.flavours) >= config.target) {
        // Prefer trimming non-Mango first so Mango stays in the mix longer
        const donor =
          config.flavours.find(
            (flavour) =>
              flavour.handle !== handle &&
              (flavour.slug || '') !== 'mango' &&
              getQty(root, flavour.handle) > 0
          ) ||
          config.flavours.find((flavour) => flavour.handle !== handle && getQty(root, flavour.handle) > 0);
        if (!donor) return;
        setQty(root, donor.handle, getQty(root, donor.handle) - 1);
      }
      setQty(root, handle, current + 1);
    }
    updateTotals(root, config);
  });
}

function init() {
  document.querySelectorAll('[data-ff-landing]').forEach((root) => {
    if (root instanceof HTMLElement) initRoot(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
